import type { Feature, FeatureCollection, MultiLineString } from 'geojson';

import type { BBox, TrackPoints } from '@/lib/db';
import { COVERAGE_GAP_MS } from '@/lib/parse';

import { douglasPeucker, epsilonForZoom } from './simplify';

/**
 * Every flight at once: the geometry behind the overview map at the top of the
 * flights page.
 *
 * This is a different problem from `segments.ts`, which draws *one* track in as
 * much detail as the current zoom deserves and recolours it by a metric. Here
 * there are hundreds of tracks on screen at a national scale, nobody is reading
 * an altitude off them, and the thing a pilot is actually looking for is the
 * shape of where they fly — so each track is reduced once, hard, to a plain
 * line, and the overlap between them is what carries the information.
 *
 * Reduced *once*, not per zoom, which is the deliberate difference. Re-running
 * Douglas–Peucker over every track on every `zoomend` would mean re-reading
 * hundreds of point arrays out of IndexedDB for a view whose whole job is to be
 * glanced at; the cost of the fixed tolerance below is a line that cuts a
 * corner by a few hundred metres once you zoom right in, and at that zoom the
 * flight's own page is one click away and draws it properly.
 */

/**
 * The zoom the overview simplifies for: about 150 m of deviation.
 *
 * Chosen above the zoom the map actually opens at (a whole country fits around
 * zoom 5) so that zooming in on one corner of the map does not immediately show
 * the simplification. Every track is reduced to this one tolerance, so a
 * 20k-fix cross-country and a 300-fix circuit cost about the same to draw.
 */
export const OVERLAY_SIMPLIFY_ZOOM = 10;

export const OVERLAY_EPSILON_DEG = epsilonForZoom(OVERLAY_SIMPLIFY_ZOOM);

/**
 * One track, ready to draw. `lines` is a `MultiLineString`'s coordinates: more
 * than one when a coverage gap splits the flight, empty when the track has no
 * two fixes to join.
 *
 * `colour` is geometry's business only in the sense that this is where it rides
 * to the map — the colour itself is computed by the caller (`FlightsOverviewMap`,
 * from where each flight falls in the drawn set's date range) because this
 * module has no idea what a flight's date is, only what its points look like.
 * `undefined` draws the flat `routeColour` fallback, which is what a single
 * flight, or every drawn flight sharing one instant, gets — a gradient across a
 * range of one is not a gradient.
 */
export interface TrackRoute {
  trackId: string;
  lines: [number, number][][];
  /** Of the simplified geometry, which is what is drawn — not the stored track bbox. */
  bbox: BBox;
  colour?: string;
}

export interface RouteProperties {
  trackId: string;
  colour?: string;
}

export type RouteFeature = Feature<MultiLineString, RouteProperties>;
export type RouteCollection = FeatureCollection<MultiLineString, RouteProperties>;

/** Cumulative count of gaps longer than `gapMs` at or before each index. */
function gapPrefix(t: Float64Array, gapMs: number): Uint32Array {
  const prefix = new Uint32Array(t.length);
  for (let i = 1; i < t.length; i += 1) {
    const dt = (t[i] ?? 0) - (t[i - 1] ?? 0);
    prefix[i] = (prefix[i - 1] ?? 0) + (dt > gapMs ? 1 : 0);
  }
  return prefix;
}

/**
 * A track's fixes as simplified `[lon, lat]` lines, broken at coverage gaps.
 *
 * The break matters more here than on the flight page, not less. An ADS-B track
 * that loses the receiver for half an hour would otherwise draw a dead-straight
 * leg across it, and on a map holding every flight a pilot has ever made, a
 * dozen of those straight lines read as real routes they never flew.
 *
 * Whether a gap sits between two *kept* fixes is decided on the full array, not
 * on the time between the kept ones: straight-and-level cruise keeps very few
 * fixes, so the gap between two of them is routinely minutes long with nothing
 * missing at all.
 */
export function routeLines(
  points: TrackPoints,
  epsilonDeg: number = OVERLAY_EPSILON_DEG,
  gapMs: number = COVERAGE_GAP_MS,
): [number, number][][] {
  const kept = douglasPeucker(points.lat, points.lon, epsilonDeg);
  if (kept.length < 2) {
    return [];
  }
  const prefix = gapPrefix(points.t, gapMs);

  const lines: [number, number][][] = [];
  let current: [number, number][] = [];
  let previous: number | undefined;

  for (const index of kept) {
    const lon = points.lon[index];
    const lat = points.lat[index];
    if (lon === undefined || lat === undefined) {
      continue;
    }
    const brokeHere = previous !== undefined && (prefix[index] ?? 0) > (prefix[previous] ?? 0);
    if (brokeHere) {
      if (current.length >= 2) {
        lines.push(current);
      }
      current = [];
    }
    current.push([lon, lat]);
    previous = index;
  }
  if (current.length >= 2) {
    lines.push(current);
  }
  return lines;
}

/** `[west, south, east, north]` around every coordinate in `lines`, or `null` if there are none. */
export function boundsForLines(lines: readonly (readonly [number, number][])[]): BBox | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const line of lines) {
    for (const [lon, lat] of line) {
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  }
  return west === Infinity ? null : [west, south, east, north];
}

/** The route for one track, or `null` when nothing is left to draw. */
export function buildRoute(
  trackId: string,
  points: TrackPoints,
  epsilonDeg: number = OVERLAY_EPSILON_DEG,
): TrackRoute | null {
  const lines = routeLines(points, epsilonDeg);
  const bbox = boundsForLines(lines);
  if (bbox === null) {
    return null;
  }
  return { trackId, lines, bbox };
}

/** One `MultiLineString` feature per track, keyed by `trackId` so a hover can name it. */
export function buildRouteCollection(routes: readonly TrackRoute[]): RouteCollection {
  return {
    type: 'FeatureCollection',
    features: routes.map((route) => ({
      type: 'Feature',
      properties: {
        trackId: route.trackId,
        ...(route.colour === undefined ? {} : { colour: route.colour }),
      },
      geometry: { type: 'MultiLineString', coordinates: route.lines },
    })),
  };
}

/** The union of every route's bounds — what the overview fits itself to. */
export function boundsForRoutes(routes: readonly TrackRoute[]): BBox | null {
  return boundsForLines(
    routes.map((route) => [
      [route.bbox[0], route.bbox[1]],
      [route.bbox[2], route.bbox[3]],
    ]),
  );
}

/**
 * The fallback colour, per theme, for a route with no `colour` of its own.
 *
 * MapLibre paints to WebGL and cannot read a CSS custom property, so these are
 * the literal sRGB values of `--accent` from `globals.css` in each theme —
 * `#006cba` is `oklch(0.52 0.15 248)`, `#5cabf2` is `oklch(0.72 0.13 248)`.
 * Same colour as every accent in the app.
 *
 * This used to be the *only* colour a route was drawn in — one accent, and a
 * track was not a second one, with emphasis carried by width and opacity
 * alone. `FlightsOverviewMap` now colours by where a flight falls in the drawn
 * set's date range, because recency is a metric that means something across a
 * hundred flights at once in a way altitude or speed never would. This is what
 * a route still gets when that colour is absent: one flight on its own, or a
 * search result that all landed at the same instant.
 */
export const ROUTE_COLOUR_LIGHT = '#006cba';
export const ROUTE_COLOUR_DARK = '#5cabf2';

export function routeColour(dark: boolean): string {
  return dark ? ROUTE_COLOUR_DARK : ROUTE_COLOUR_LIGHT;
}

/**
 * How faint one track is. Low enough that a hundred overlapping circuits at
 * the home aerodrome read as a dense knot rather than a solid blob, high
 * enough that a single ferry flight across the country is still visible on
 * its own.
 */
export const ROUTE_OPACITY = 0.45;
export const ROUTE_WIDTH = 1.6;
/** The hovered track, lifted out of the pile. */
export const ROUTE_HIGHLIGHT_WIDTH = 3.4;
